import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DataTable,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useIsMobile,
  type DataTableViewMode,
} from "@geti/identidade-visual-react";
import styles from "./DataTableComponents.module.css";
import { MdDownload, MdPictureAsPdf } from "react-icons/md";
import { MoreHorizontal, Trash2 } from "lucide-react";
import services from "../../../Services/services";
import { fetchDeleteFile } from "../../../Services/Slices/deleteFileSlice";
import { useDispatch } from "react-redux";
import { fetchDeletePublishedFile } from "../../../Services/Slices/deletePublishedFileSlice";
import { returnHandleExtractUrl } from "../../../Components/Helper";

const PAGE_SIZE = 5;
const DELETE_CONFIRM_SECONDS = 5;
const COLUMN_VISIBILITY_STORAGE_KEY = "diario-oficial-home-editions-columns";

const ACTION_COLUMNS = new Set([
  "view",
  "presigned_url",
  "delete",
  "deletePublished",
  "actions",
]);

function getColumnMeta(property: string) {
  if (property === "date") return { className: "col-date" };
  if (property === "type") return { className: "col-type" };
  if (property === "edition" || property === "name") {
    return { className: "col-edition" };
  }
  if (ACTION_COLUMNS.has(property)) return { className: "col-action" };
  return undefined;
}

interface TableColumn {
  title: string;
  property: string;
}

interface TableRow {
  edition?: string;
  date?: string;
  name?: string;
  presigned_url?: string;
  fileKey?: string;
  delete?: string;
  [key: string]: unknown;
}

interface TableProps {
  title?: string;
  showTitle?: boolean;
  columns: TableColumn[];
  data: TableRow[];
  setPage: (page: number) => void;
  page: number;
  downloadButton?: boolean;
  total?: number;
  isEmpty?: boolean;
  isStatus?: boolean;
  loading?: boolean;
  error?: boolean;
  filtersContent?: ReactNode;
}

type PendingDelete = {
  kind: "delete" | "deletePublished";
  row: TableRow;
};

function formatPublicationDate(dateStr: string): string {
  if (!dateStr) return dateStr;
  const parts = dateStr.split("/").map(Number);
  if (parts.length < 3) return dateStr;
  const [day, month, year] = parts;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return dateStr;
  const formatted = date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function getFileKey(row: TableRow) {
  if (!row.presigned_url) {
    return "";
  }
  const match = row.presigned_url.match(/posts\/([^?]+)/);
  return match ? match[1] : "";
}

function redirectToPdfViewer(row: TableRow) {
  let cleanFileKey = "";
  if (row.fileKey) {
    cleanFileKey = String(row.fileKey).replace("posts/", "");
  } else {
    cleanFileKey = getFileKey(row);
  }
  if (!cleanFileKey) {
    return;
  }
  window.open(`/pdf-viewer/${cleanFileKey}`, "_blank");
}

const DataTableComponents = ({
  title,
  showTitle = true,
  columns,
  data,
  setPage,
  page,
  downloadButton,
  total,
  isEmpty,
  loading,
  error,
  filtersContent,
}: TableProps) => {
  const dispatch = useDispatch<any>();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState<DataTableViewMode>(
    isMobile ? "cards" : "table"
  );
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(0);

  useEffect(() => {
    if (!pendingDelete) {
      setDeleteCountdown(0);
      return;
    }

    setDeleteCountdown(DELETE_CONFIRM_SECONDS);
    const intervalId = window.setInterval(() => {
      setDeleteCountdown((seconds) => {
        if (seconds <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [pendingDelete]);

  const handleConfirmDelete = () => {
    if (deleteCountdown > 0) return;
    if (!pendingDelete) return;

    const { kind, row } = pendingDelete;
    if (kind === "delete") {
      dispatch(fetchDeleteFile(String(row.delete ?? "")));
    } else {
      const extractedUrl = returnHandleExtractUrl(row.presigned_url);
      dispatch(fetchDeletePublishedFile(extractedUrl ? extractedUrl : ""));
    }
    setPendingDelete(null);
  };

  const deleteDialogMessage = pendingDelete?.row.edition
    ? `Tem certeza que deseja excluir a edição "${pendingDelete.row.edition}"? Esta ação não pode ser desfeita.`
    : "Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.";

  const handleDownloadFile = (file: string) => {
    const a = document.createElement("a");
    a.href = file;
    a.download = "downloadedFile.pdf";
    a.click();
  };

  const handleDownloadTemplate = async () => {
    const file_name = "DIÁRIO OFICIAL Modelo.docx";
    const file = await services.downloadFiles(file_name);
    const a = document.createElement("a");
    a.href = file?.data?.url;
    a.download = "template.pdf";
    a.click();
  };

  const tableColumns = useMemo<ColumnDef<TableRow>[]>(
    () =>
      columns.map((column) => {
        const columnMeta = getColumnMeta(column.property);
        return {
          id: column.property,
          accessorKey: column.property,
          header: column.title,
          ...(columnMeta ? { meta: columnMeta } : {}),
          cell: ({ row }) => {
            const property = column.property;
            const rowData = row.original;

            if (property === "actions") {
              return (
                <div className={styles.actionsCell}>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      type="button"
                      className={`${styles.actionButton} ${styles.actionsTrigger}`}
                      aria-label="Ações"
                    >
                      <MoreHorizontal className="size-4" aria-hidden />
                    </DropdownMenuTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => redirectToPdfViewer(rowData)}
                      >
                        <MdPictureAsPdf aria-hidden />
                        Visualizar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          handleDownloadFile(String(rowData.presigned_url ?? ""))
                        }
                      >
                        <MdDownload aria-hidden />
                        Baixar arquivo
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() =>
                          setPendingDelete({
                            kind: "deletePublished",
                            row: rowData,
                          })
                        }
                      >
                        <Trash2 aria-hidden />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenu>
                </div>
              );
            }

            if (property === "presigned_url") {
              return (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={`mx-auto ${styles.actionButton}`}
                  onClick={() =>
                    handleDownloadFile(String(rowData.presigned_url ?? ""))
                  }
                  aria-label="Baixar arquivo"
                >
                  <MdDownload className="size-5" aria-hidden />
                </Button>
              );
            }

            if (property === "view") {
              return (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={`mx-auto ${styles.actionButton}`}
                  onClick={() => redirectToPdfViewer(rowData)}
                  aria-label="Visualizar PDF"
                >
                  <MdPictureAsPdf className="size-5" aria-hidden />
                </Button>
              );
            }

            if (property === "delete" || property === "deletePublished") {
              return (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className={`mx-auto ${styles.deleteButton}`}
                  onClick={() =>
                    setPendingDelete({
                      kind: property as "delete" | "deletePublished",
                      row: rowData,
                    })
                  }
                  aria-label="Excluir"
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              );
            }

            const raw = String(rowData[property] ?? "");
            const text =
              property === "date" ? formatPublicationDate(raw) : raw;

            if (
              property === "date" ||
              property === "edition" ||
              property === "name" ||
              property === "type"
            ) {
              return (
                <span className="block truncate text-sm" title={text}>
                  {text}
                </span>
              );
            }

            return <span className="text-sm">{text}</span>;
          },
        };
      }),
    [columns]
  );

  const columnLabels = useMemo(
    () =>
      Object.fromEntries(columns.map((column) => [column.property, column.title])),
    [columns]
  );

  const displayData = isEmpty || error ? [] : data ?? [];
  const emptyMessage = error
    ? "Não foi possível carregar as informações!"
    : "Não foi encontrado nenhum conteúdo";

  const totalRowCount = total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRowCount / PAGE_SIZE));

  const toolbarExtra = downloadButton ? (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      onClick={handleDownloadTemplate}
      aria-label="Baixar template"
    >
      <MdDownload className="size-5" aria-hidden />
    </Button>
  ) : undefined;

  return (
    <div className={styles.wrapper}>
      {showTitle && title && (
        <h2 className={styles.title}>{title}</h2>
      )}
      <DataTable
        columns={tableColumns}
        data={displayData}
        filters={[]}
        filtersContent={filtersContent}
        toolbarExtra={toolbarExtra}
        emptyMessage={emptyMessage}
        columnLabels={columnLabels}
        columnVisibilityStorageKey={COLUMN_VISIBILITY_STORAGE_KEY}
        pageSize={PAGE_SIZE}
        paginationMode="server"
        serverPagination={{
          pageIndex: page - 1,
          pageCount,
          totalRowCount,
          pageSize: PAGE_SIZE,
          onPageChange: (pageIndex) => setPage(pageIndex + 1),
        }}
        isLoadingInitial={!!loading && displayData.length === 0}
        isLoading={!!loading && displayData.length > 0}
        loadingInitialText="Carregando…"
        loadingRefreshText="Atualizando…"
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showViewModeToggle
      />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>{deleteDialogMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingDelete(null)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteCountdown > 0}
            >
              {deleteCountdown > 0
                ? `Excluir (${deleteCountdown}s)`
                : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataTableComponents;
